import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardDrive, Search, X } from "lucide-react";

/**
 * Buscador de equipo del catálogo central.
 *  - Se filtra por cliente_id + sucursal seleccionados arriba en el formulario.
 *  - Al elegir un equipo, llama onSelect(equipo) para que el editor autocomplete
 *    marca, modelo, serie, código, box, tipo de equipo, etc.
 *  - Si el usuario no encuentra el equipo, simplemente cierra el panel y
 *    sigue completando los campos a mano (se creará automáticamente al
 *    completar el reporte gracias al upsert del backend).
 */
export default function EquipoSelect({ clienteId, sucursal, onSelect, label = "Buscar equipo del catálogo", disabled = false }) {
  const { api } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!clienteId) { setResults([]); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { cliente_id: clienteId, limit: 25 };
        if (sucursal) params.sucursal = sucursal;
        if (q.trim()) params.q = q.trim();
        const res = await api.get("/api/equipos/find", { params });
        setResults(res.data || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [clienteId, sucursal, q, api]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (eq) => {
    onSelect?.(eq);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <Label className="text-xs text-gray-500 flex items-center gap-1.5">
        <HardDrive className="h-3.5 w-3.5" /> {label}
      </Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          value={q}
          disabled={disabled || !clienteId}
          placeholder={!clienteId ? "Selecciona cliente primero" : "Marca, modelo, serie, código o box…"}
          onFocus={() => clienteId && setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          className="pl-10 h-11"
          autoComplete="off"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Limpiar"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && clienteId && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-gray-400">Buscando…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-gray-500">
              No hay equipos para esos filtros. Completa los campos manualmente y se creará al finalizar el reporte.
            </div>
          )}
          {!loading && results.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => pick(e)}
              className="w-full text-left px-3 py-2 hover:bg-[#F5F7FA] border-b last:border-b-0"
            >
              <div className="text-sm font-medium text-gray-800 truncate">
                {[e.marca, e.modelo].filter(Boolean).join(" ") || "(sin marca/modelo)"}
                {e.estado === "de_baja" && (
                  <span className="ml-2 text-[10px] text-gray-500 uppercase">De baja</span>
                )}
              </div>
              <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                {e.numero_serie && <span>Serie: {e.numero_serie}</span>}
                {e.codigo_interno && <span>Código: {e.codigo_interno}</span>}
                {e.numero_box && <span>Box: {e.numero_box}</span>}
                {e.sucursal && <span>Sucursal: {e.sucursal}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
